import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "./components/Modal";
import RtlsMap from "./components/rtlsMap";
import { useIsMobile } from "./hooks/useIsMobile";
import {
  BuildingInProduction,
  OnlineMods,
  ActivePersonnel,
  Noncompliances,
  ModRollTime,
  WeeklyProgress,
} from "./components/modalContent";
import useHomepageData from "./hooks/useHomepageData";
import useAvgCount from "./hooks/useAvgCount";
import "./homePage.css";
import "../animation.css";

const HomePage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedMetric, setSelectedMetric] = useState(null);

  // ── Live homepage metrics ──────────────────────────────────────────────────
  // Replaces the module-level mock derivations (uniqueProperties, onlineModCount,
  // activePersonnelCount). sevenDayAvg values may be null when the backend has
  // no data in the window — render "—" rather than "0" for those fields.
  const {
    buildings,
    modsOnLine,
    activePersonnel,
    failedJobsToday,
    avgDailyRollTime,
    weeklyProgressPct,
    loading: metricsLoading,
    error: metricsError,
  } = useHomepageData();

  // ── 7-business-day averages (avg-count route) ──────────────────────────────
  // Independent fetch — a failure here never blocks the live current-value
  // metrics above from rendering. avgsLoading stays true only until the
  // avg-count response arrives; comparison rows render "—" in the interim.
  const {
    onlineMods: avgOnlineMods,
    activePersonnel: avgActivePersonnel,
    noncompliances: avgNoncompliances,
    modRolls: avgModRolls,
    loading: avgsLoading,
  } = useAvgCount();

  // ── Derived display values ─────────────────────────────────────────────────
  // fmtAvgDelta returns { label, cls } so each comparison span gets both the
  // signed percentage string and the correct CSS modifier class in one call,
  // rather than hardcoding "positive" / "negative" per card.
  //
  //   label  "+X%" | "-X%" | "—"
  //   cls    "positive" | "negative" | "neutral"
  //
  // "—" (null avg or zero avg) → neutral so the dash renders without colour.
  // For noncompliances the sign convention inverts: fewer noncompliances today
  // than the avg is good (+%), more is bad (-%). The card's CSS class reflects
  // the raw arithmetic sign; semantic colour inversion (if needed) is a CSS
  // concern, not a logic concern here.
  const fmtAvgDelta = (current, avg) => {
    if (avg == null || avg === 0) return { label: "—", cls: "neutral" };
    const delta = Math.round(((current - avg) / avg) * 100);
    if (delta > 0) return { label: `+${delta}%`, cls: "positive" };
    if (delta < 0) return { label: `${delta}%`, cls: "negative" };
    return { label: "0%", cls: "neutral" };
  };

  // Each delta reads from useAvgCount (avgCounts.*) — the real 7-business-day
  // stored averages — instead of the always-null sevenDayAvg fields that
  // useHomepageData previously exposed for these metrics.
  const onlineModsDelta = fmtAvgDelta(modsOnLine.current, avgOnlineMods);
  const activePersonnelDelta = fmtAvgDelta(
    activePersonnel.current,
    avgActivePersonnel,
  );
  const noncomplDelta = fmtAvgDelta(failedJobsToday.current, avgNoncompliances);

  // Roll time: null current means < 2 rolls today (no inter-roll gap yet).
  // Pass 0 as the current value when null so fmtAvgDelta produces "—" via the
  // avg == null guard rather than a misleading −100% against a real average.
  const rollTimeDisplay =
    avgDailyRollTime.current != null ? String(avgDailyRollTime.current) : "—";
  const rollTimeDelta = fmtAvgDelta(avgDailyRollTime.current ?? 0, avgModRolls);

  // Weekly progress as a rounded integer percentage string.
  const weeklyPct = Math.round(weeklyProgressPct.current);

  const modalConfig = {
    "building-in-production": {
      title: "Current Building in Production",
      component: BuildingInProduction,
      size: "large",
    },
    "online-mods": {
      title: "Online Mods",
      component: OnlineMods,
      size: "large",
    },
    "active-personnel": {
      title: "Active Personnel",
      component: ActivePersonnel,
      size: "medium",
    },
    noncompliances: {
      title: "Today's Noncompliances",
      component: Noncompliances,
      size: "xlarge",
    },
    "mod-roll-time": {
      title: "Daily Mod Roll Time",
      component: ModRollTime,
      size: "large",
    },
    "weekly-progress": {
      title: "Weekly Production Progress",
      component: WeeklyProgress,
      size: "large",
    },
  };

  const handleMetricClick = (metricId) => {
    setSelectedMetric(metricId);
  };

  const closeModal = () => {
    setSelectedMetric(null);
  };

  const currentModal = selectedMetric ? modalConfig[selectedMetric] : null;
  const ModalContent = currentModal?.component;

  return (
    <div className="home-page page-enter">
      <div className="page-header">
        <h1>RTLS Dashboard Overview</h1>
        <p className="page-subtitle">
          Real-time production monitoring and metrics
        </p>
      </div>

      {/*
        home-content is a 2-column grid on desktop (map + metrics).
        On mobile the map is omitted so the grid collapses to 1 column
        and the metrics fill the full viewport height - see homePage.css
        @media (max-width: 480px) for the layout rules.
      */}
      <div className={`home-content${isMobile ? " home-content--no-map" : ""}`}>
        {/* RTLS Map - rendered only on desktop to save resources on mobile.
            On mobile, users navigate to /rtls-map via the hamburger menu. */}
        {!isMobile && <RtlsMap isHomePage={true} filterQuery={""} />}

        {/* Metrics Grid */}
        <div className="metrics-grid">
          {/* ── Building in Production ────────────────────────────────────── */}
          <div
            className="metric-card"
            onClick={() => handleMetricClick("building-in-production")}
          >
            <div className="metric-header">
              <h3>Current Building in Production</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                <path d="M9 22v-4h6v4" />
                <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
              </svg>
            </div>
            <div className="metric-value-string">
              <div className="string-list">
                {metricsLoading ? (
                  <span className="list-item">—</span>
                ) : (
                  buildings.current.map((property) => (
                    <span key={property} className="list-item">
                      {property}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          {/* ── Online Mods ───────────────────────────────────────────────── */}
          <div
            className="metric-card"
            onClick={() => handleMetricClick("online-mods")}
          >
            <div className="metric-header">
              <h3>Online Mods</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">
                {metricsLoading ? "—" : modsOnLine.current}
              </span>
              <span className="value-label">units on line</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className={`comparison-value ${onlineModsDelta.cls}`}>
                {metricsLoading || avgsLoading ? "—" : onlineModsDelta.label}
              </span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          {/* ── Active Personnel ──────────────────────────────────────────── */}
          <div
            className="metric-card"
            onClick={() => handleMetricClick("active-personnel")}
          >
            <div className="metric-header">
              <h3>Active Personnel</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">
                {metricsLoading ? "—" : activePersonnel.current}
              </span>
              <span className="value-label">active in last 30min</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className={`comparison-value ${activePersonnelDelta.cls}`}>
                {metricsLoading || avgsLoading
                  ? "—"
                  : activePersonnelDelta.label}
              </span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          {/* ── Noncompliances ────────────────────────────────────────────── */}
          <div
            className="metric-card alert"
            onClick={() => handleMetricClick("noncompliances")}
          >
            <div className="metric-header">
              <h3>Today's Noncompliances</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">
                {metricsLoading ? "—" : failedJobsToday.current}
              </span>
              <span className="value-label">failed jobs</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className={`comparison-value ${noncomplDelta.cls}`}>
                {metricsLoading || avgsLoading ? "—" : noncomplDelta.label}
              </span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for compliance dashboard</span>
            </div>
          </div>

          {/* ── Mod Roll Time ─────────────────────────────────────────────── */}
          <div
            className="metric-card"
            onClick={() => handleMetricClick("mod-roll-time")}
          >
            <div className="metric-header">
              <h3>Daily Mod Roll Time</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">
                {metricsLoading ? "—" : rollTimeDisplay}
              </span>
              <span className="value-label">mins avg</span>
            </div>
            <div className="metric-comparison">
              <span className="comparison-label">7-day avg:</span>
              <span className={`comparison-value ${rollTimeDelta.cls}`}>
                {metricsLoading || avgsLoading ? "—" : rollTimeDelta.label}
              </span>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for details</span>
            </div>
          </div>

          {/* ── Weekly Progress ───────────────────────────────────────────── */}
          <div
            className="metric-card"
            onClick={() => handleMetricClick("weekly-progress")}
          >
            <div className="metric-header">
              <h3>Weekly Production Progress</h3>
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="20" x2="12" y2="10" />
                <line x1="18" y1="20" x2="18" y2="4" />
                <line x1="6" y1="20" x2="6" y2="16" />
              </svg>
            </div>
            <div className="metric-value">
              <span className="value-primary">
                {metricsLoading ? "—" : `${weeklyPct}%`}
              </span>
              <span className="value-label">weighted avg of weekly goals</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: metricsLoading ? "0%" : `${weeklyPct}%` }}
              ></div>
            </div>
            <div className="metric-footer">
              <span className="click-hint">Click for goal breakdown</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error banner - shown beneath the grid if the metrics fetch failed.
          Does not block the rest of the page from rendering. */}
      {metricsError && (
        <p className="metrics-error-banner">
          Failed to load metrics: {metricsError}
        </p>
      )}

      {/* Modal - close animation handled inside Modal.js via isClosing state */}
      <Modal
        isOpen={selectedMetric !== null}
        onClose={closeModal}
        title={currentModal?.title}
        size={currentModal?.size}
      >
        {ModalContent && <ModalContent />}
      </Modal>
    </div>
  );
};

export default HomePage;
